import type { ISODateString, UUID } from "./common.types";

export type PlanType = "free" | "lite" | "plus" | "pro" | "agency";
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "unpaid";

export interface Subscription {
  id: UUID;
  user_id: UUID;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  plan_type: PlanType;
  status: SubscriptionStatus;
  current_period_end: ISODateString | null;
  cancel_at_period_end: boolean;
  grace_period_ends_at: ISODateString | null;
  created_at: ISODateString;
  updated_at: ISODateString;
}

export interface PlanConfig {
  type: PlanType;
  nameKey: string;
  priceEur: number;
  tourLimit: number | null;
  /** Storage cap in bytes. null = unlimited. Authoritative value comes from the
   *  get_storage_usage() RPC (plan_storage_limit en SQL); this mirrors it for
   *  labels/fallback y debe coincidir con pricing-interno.md §2. */
  storageLimitBytes: number | null;
  /** Horas de cambios de producción incluidas por trimestre (mantenimiento). null = a medida. */
  featureKeys: string[];
  stripePriceId: string;
}

const GB = 1024 * 1024 * 1024;

// Cada tier interno ES un nivel de mantenimiento (docs/01.Legal & Finance/pricing-interno.md §2):
// free=Base, lite=Negocio, plus=Profesional, pro=Empresa. El cliente solo ve el nombre del nivel.
export const PLAN_CONFIGS: PlanConfig[] = [
  {
    type: "free",
    nameKey: "pricing_nivel_base",
    priceEur: 9.99,
    tourLimit: 1,
    storageLimitBytes: 2 * GB,
    // Los highlights del nivel dicen lo que el plan realmente define: tours,
    // storage y (en Pro+) la marca del viewer. Ninguna feature se gatea.
    featureKeys: [
      "pricing_hl_free_1",
      "pricing_hl_free_2",
      "pricing_hl_free_3",
    ],
    stripePriceId: "",
  },
  {
    type: "lite",
    nameKey: "pricing_nivel_negocio",
    priceEur: 24.99,
    tourLimit: 5,
    storageLimitBytes: 10 * GB,
    featureKeys: [
      "pricing_hl_lite_1",
      "pricing_hl_lite_2",
      "pricing_hl_lite_3",
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PRICE_LITE ?? "",
  },
  {
    type: "plus",
    nameKey: "pricing_nivel_profesional",
    priceEur: 49.99,
    tourLimit: 20,
    storageLimitBytes: 40 * GB,
    featureKeys: [
      "pricing_hl_plus_1",
      "pricing_hl_plus_2",
      "pricing_hl_plus_3",
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PRICE_PLUS ?? "",
  },
  {
    type: "pro",
    nameKey: "pricing_nivel_empresa",
    priceEur: 99.99,
    tourLimit: 75,
    storageLimitBytes: 150 * GB,
    featureKeys: [
      "pricing_hl_pro_1",
      "pricing_hl_pro_2",
      "pricing_hl_pro_3",
      "pricing_hl_pro_4",
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PRICE_PRO ?? "",
  },
  {
    type: "agency",
    nameKey: "pricing_agency",
    priceEur: 179,
    tourLimit: null,
    storageLimitBytes: null,
    featureKeys: [
      "pricing_hl_agency_1",
      "pricing_hl_agency_2",
      "pricing_hl_agency_3",
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PRICE_AGENCY ?? "",
  },
];

export function isSubscriptionBlocking(sub: Subscription | null): boolean {
  if (!sub) return true;
  if (sub.status === "unpaid") return true;
  if (sub.status === "canceled") {
    if (!sub.grace_period_ends_at) return true;
    return new Date() > new Date(sub.grace_period_ends_at);
  }
  return false;
}
